import { AFFILIATE_REL, isAffiliateHref, toAffiliateHref } from '../lib/affiliateLinks';

/**
 * Determines if a URL is an internal link (should open in same tab)
 * 
 * Internal links:
 * - Links starting with `/` (e.g., `/blog`, `/heavenpedia`)
 * - Links to heavengirlfriend.com (e.g., `https://heavengirlfriend.com/blog`)
 * 
 * External links:
 * - All other links should open in a new tab
 */
export const isInternalLink = (href: string): boolean => {
  if (!href) return false;
  
  // Links starting with / are internal
  if (href.startsWith('/')) return true;
  
  // Check if the URL's domain is heavengirlfriend.com (not just anywhere in the string)
  // This prevents URLs with heavengirlfriend.com in query params from being treated as internal
  try {
    const url = new URL(href);
    if (url.hostname === 'heavengirlfriend.com' || url.hostname === 'www.heavengirlfriend.com') {
      return true;
    }
  } catch {
    // Not a valid absolute URL, check if it starts with the domain
    if (href.startsWith('https://heavengirlfriend.com') || href.startsWith('http://heavengirlfriend.com')) {
      return true;
    }
  }
  
  // Everything else is external
  return false;
};

/**
 * Extracts the path from a full URL if it's a heavengirlfriend.com link,
 * otherwise returns the href as-is
 */
export const extractInternalPath = (href: string): string => {
  // Only extract path if the domain is heavengirlfriend.com
  // This prevents URLs with heavengirlfriend.com in query params from being modified
  try {
    const url = new URL(href);
    if (url.hostname === 'heavengirlfriend.com' || url.hostname === 'www.heavengirlfriend.com') {
      return url.pathname + url.search + url.hash || '/';
    }
  } catch {
    // Not a valid absolute URL, check if it starts with the domain
    if (href.startsWith('https://heavengirlfriend.com') || href.startsWith('http://heavengirlfriend.com')) {
      return href.replace(/^https?:\/\/[^/]+/, '') || '/';
    }
  }
  return href;
};

/**
 * Processes all links in HTML content to ensure proper target attributes.
 * Internal links open in the same tab, external links open in a new tab.
 * 
 * @param htmlContent - HTML string to process
 * @returns Processed HTML string with updated link attributes
 */
export const processLinksInHtml = (htmlContent: string): string => {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return htmlContent; // SSR safety
  
  try {
    const parser = new DOMParser();
    // Wrap content in a container to handle fragments
    const wrappedContent = `<div>${htmlContent}</div>`;
    const doc = parser.parseFromString(wrappedContent, 'text/html');
    
    const container = doc.querySelector('div');
    if (!container) return htmlContent;
    
    const links = Array.from(container.querySelectorAll('a'));
    
    links.forEach(link => {
      const originalHref = link.getAttribute('href');
      if (!originalHref) return;

      const href = toAffiliateHref(originalHref);
      if (!href) return;

      if (href !== originalHref) {
        link.setAttribute('href', href);
      }

      const isInternal = isInternalLink(href);
      const isAffiliate = isAffiliateHref(href);

      // Button-style affiliate anchors can come from raw HTML content, so enforce
      // sponsored affiliate rel before skipping normal content-link handling.
      if (
        isAffiliate &&
        (link.classList.contains('btn-primary') || link.classList.contains('affiliate-button'))
      ) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', AFFILIATE_REL);
        return;
      }

      // Skip non-affiliate button links that are handled by their own styling/logic.
      if (link.classList.contains('btn-primary') ||
          link.classList.contains('affiliate-button')) {
        return;
      }
      
      if (isInternal) {
        // Internal content links should stay in the same tab unless they are affiliate redirects.
        if (isAffiliate) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', AFFILIATE_REL);
        } else {
          link.removeAttribute('target');
          link.removeAttribute('rel');
        }

        // Convert heavengirlfriend.com URLs to relative paths
        if (href.includes('heavengirlfriend.com')) {
          const relativePath = extractInternalPath(href);
          link.setAttribute('href', relativePath);
        }
        
        // Add data attribute to mark as internal content link
        link.setAttribute('data-internal-link', 'true');
      } else {
        // External links: ensure target="_blank" and proper rel attributes
        link.setAttribute('target', '_blank');
        
        // Preserve existing rel attributes or set default
        const existingRel = link.getAttribute('rel') || '';
        const relParts = existingRel.split(' ').filter(Boolean);
        
        // Add noopener and noreferrer if not present
        if (!relParts.includes('noopener')) relParts.push('noopener');
        if (!relParts.includes('noreferrer')) relParts.push('noreferrer');
        if (isAffiliate) {
          if (!relParts.includes('nofollow')) relParts.push('nofollow');
          if (!relParts.includes('sponsored')) relParts.push('sponsored');
        }
        
        link.setAttribute('rel', relParts.join(' '));
      }
    });
    
    return container.innerHTML;
  } catch (error) {
    console.error('Error processing links in HTML:', error);
    return htmlContent; // Return original content on error
  }
};

